/**
 * The tapes: every LTO cartridge of the archive as on the shelf (its barcode label, its generation's colour), with
 * how many items it holds, how many can be previewed, the years it covers, where it is and how full. A tape's panel
 * lists its items and lets the archivist keep its generation, place, slot, state and notes.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { LTO_GENERATIONS, LTO_NATIVE, TAPE_LOCATIONS, TAPE_STATES, formatBytes, type LtoGeneration } from '@airpost/shared';
import { ApiError, get, put } from '../api.js';
import { t } from '../i18n.js';
import { Cartridge, TapeRobot } from '../tape-robot.js';
import { fmtDateInput, fmtDateTime, hhmmss } from '../util.js';
import { locationLabel } from './archive-item.js';

export interface TapeDto {
  id: string;
  barcode: string;
  generation: string | null;
  capacityBytes: number | null;
  usedBytes: number;
  state: 'active' | 'full' | 'retired';
  location: string;
  slot: string | null;
  notes: string | null;
  lastWrittenAt: string | null;
  isDemo: boolean;
  items?: number;
  proxies?: number;
  fileBytes?: number;
  firstDate?: string | null;
  lastDate?: string | null;
}
interface TapeItem {
  id: string;
  title: string;
  eventDate: string;
  itemNo: number | null;
  durationS: number;
  videoType: string | null;
  fileName: string | null;
  fileSize: number | null;
  archivedAt: string | null;
  hasProxy: boolean;
}

const stateLabel = (s: string) => ({ active: t('ltoStateActive'), full: t('ltoStateFull'), retired: t('ltoStateRetired') })[s] ?? s;
const capacityOf = (tp: TapeDto) => tp.capacityBytes ?? (tp.generation && tp.generation in LTO_NATIVE ? LTO_NATIVE[tp.generation as LtoGeneration].capacityBytes : null);
/** The days a tape covers, as short as it reads: 23/10/2026, 23–24/10/2026, 19/10–23/11/26, 2016–2019. */
export function tapeSpan(a?: string | null, b?: string | null): string {
  if (!a || !b) return '—';
  const [ya, ma, da] = a.split('-');
  const [yb, mb, db] = b.split('-');
  if (ya !== yb) return `${ya}–${yb}`;
  if (a === b) return fmtDateInput(a);
  if (ma === mb) return `${Number(da)}–${Number(db)}/${mb}/${yb}`;
  return `${da}/${ma}–${db}/${mb}/${yb!.slice(2)}`;
}

function Fill({ tape }: { tape: TapeDto }) {
  const cap = capacityOf(tape);
  if (!cap) return tape.usedBytes ? <span className="muted small">{formatBytes(tape.usedBytes)}</span> : null;
  const pct = Math.min(100, (tape.usedBytes / cap) * 100);
  return (
    <span className="lto-fill" title={`${formatBytes(tape.usedBytes)} / ${formatBytes(cap)}`}>
      <span style={{ width: `${Math.max(pct, tape.usedBytes ? 1.5 : 0)}%` }} />
      <em>
        {formatBytes(tape.usedBytes)} / {formatBytes(cap)}
      </em>
    </span>
  );
}

export function TapesView({ openTapeId, onOpenItem }: { openTapeId: string | null; onOpenItem: (videoId: string, tapeId: string) => void }) {
  const [q, setQ] = useState('');
  const [location, setLocation] = useState('');
  const [sort, setSort] = useState<'recent' | 'barcode'>('recent');
  const [tapes, setTapes] = useState<TapeDto[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(openTapeId);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ sort });
    if (q.trim()) p.set('q', q.trim());
    if (location) p.set('location', location);
    setLoading(true);
    get<{ tapes: TapeDto[] }>(`/api/archive/tapes?${p}`)
      .then((d) => {
        setTapes(d.tapes);
        setError(null);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [q, location, sort]);
  useEffect(() => {
    const h = window.setTimeout(load, q ? 250 : 0);
    return () => window.clearTimeout(h);
  }, [load, q]);

  return (
    <>
      <section className="lto-main">
        <div className="lto-toolbar">
          <input className="lto-input" type="search" placeholder={t('ltoTapeSearch')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('ltoTapeSearch')} />
          <select className="lto-input" value={location} onChange={(e) => setLocation(e.target.value)} aria-label={t('ltoLocation')}>
            <option value="">{t('ltoAllLocations')}</option>
            {TAPE_LOCATIONS.map((l) => (
              <option key={l} value={l}>
                {locationLabel(l)}
              </option>
            ))}
          </select>
          <div className="lto-seg small">
            <button type="button" className={sort === 'recent' ? 'on' : ''} onClick={() => setSort('recent')}>
              {t('ltoSortRecent')}
            </button>
            <button type="button" className={sort === 'barcode' ? 'on' : ''} onClick={() => setSort('barcode')}>
              {t('ltoSortBarcode')}
            </button>
          </div>
          <span className="muted">{tapes ? `${tapes.length} ${t('ltoTapesCount')}` : ''}</span>
        </div>
        {error && <div className="lto-err">{error}</div>}
        {!tapes && loading && <TapeRobot barcode={q || 'LTO'} caption={t('ltoRobotSearch')} />}
        <div className="lto-shelf">
          {tapes?.length === 0 && <div className="lto-empty">{t('ltoNoTapes')}</div>}
          {tapes?.map((tp) => (
            <button key={tp.id} type="button" className={`lto-tape${selected === tp.id ? ' sel' : ''}${tp.state === 'retired' ? ' retired' : ''}`} onClick={() => setSelected(tp.id)}>
              <Cartridge barcode={tp.barcode} generation={tp.generation} />
              <span className="lto-tape-info">
                <span className="lto-tape-line">
                  <b>{tp.items ?? 0}</b> {t('ltoItems')}
                  {tp.proxies ? <span className="lto-tag">▶ {tp.proxies}</span> : null}
                </span>
                <span className="lto-tape-line muted">{tapeSpan(tp.firstDate, tp.lastDate)}</span>
                <span className="lto-tape-line muted">
                  {locationLabel(tp.location)}
                  {tp.slot ? ` · ${tp.slot}` : ''}
                </span>
                <Fill tape={tp} />
              </span>
            </button>
          ))}
        </div>
      </section>
      {selected ? <TapePanel key={selected} id={selected} onOpenItem={onOpenItem} onSaved={load} /> : <aside className="lto-panel lto-panel-empty">{t('ltoPickTape')}</aside>}
    </>
  );
}

function TapePanel({ id, onOpenItem, onSaved }: { id: string; onOpenItem: (videoId: string, tapeId: string) => void; onSaved: () => void }) {
  const [d, setD] = useState<{ tape: TapeDto; items: TapeItem[]; canManage: boolean } | null>(null);
  const [form, setForm] = useState<{ generation: string; location: string; slot: string; state: string; notes: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    get<{ tape: TapeDto; items: TapeItem[]; canManage: boolean }>(`/api/archive/tapes/${id}`)
      .then((x) => {
        setD(x);
        setForm({ generation: x.tape.generation ?? '', location: x.tape.location, slot: x.tape.slot ?? '', state: x.tape.state, notes: x.tape.notes ?? '' });
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [id]);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setError(null);
    try {
      const r = await put<{ tape: TapeDto }>(`/api/archive/tapes/${id}`, { generation: form.generation || null, location: form.location, slot: form.slot || null, state: form.state, notes: form.notes || null });
      setD((x) => (x ? { ...x, tape: r.tape } : x));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };
  if (!d || !form) return <aside className="lto-panel lto-panel-empty">{error ?? t('loading')}</aside>;
  const tp = d.tape;
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => (f ? { ...f, [k]: e.target.value } : f));
  const ro = !d.canManage;
  return (
    <aside className="lto-panel">
      <div className="lto-tape-hero">
        <Cartridge barcode={tp.barcode} generation={tp.generation} size="lg" />
        <div>
          <h2 className="lto-item-title">{tp.barcode}</h2>
          <div className="lto-item-sub">
            {tp.generation ?? t('ltoGenUnknown')} · {stateLabel(tp.state)}
            {tp.isDemo && <span className="lto-tag">DEMO</span>}
          </div>
          <Fill tape={tp} />
          {tp.lastWrittenAt && (
            <div className="muted small">
              {t('ltoLastWritten')}: {fmtDateTime(tp.lastWrittenAt)}
            </div>
          )}
        </div>
      </div>
      <div className="lto-panel-body">
        <form className="lto-tape-form" onSubmit={save}>
          <label>
            {t('ltoGeneration')}
            <select value={form.generation} onChange={set('generation')} disabled={ro}>
              <option value="">{t('ltoGenUnknown')}</option>
              {LTO_GENERATIONS.map((g) => (
                <option key={g} value={g}>
                  {g} · {formatBytes(LTO_NATIVE[g].capacityBytes)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('ltoLocation')}
            <select value={form.location} onChange={set('location')} disabled={ro}>
              {TAPE_LOCATIONS.map((l) => (
                <option key={l} value={l}>
                  {locationLabel(l)}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('ltoSlot')}
            <input type="text" maxLength={60} value={form.slot} onChange={set('slot')} disabled={ro} placeholder={t('ltoSlotHint')} />
          </label>
          <label>
            {t('ltoState')}
            <select value={form.state} onChange={set('state')} disabled={ro}>
              {TAPE_STATES.map((s) => (
                <option key={s} value={s}>
                  {stateLabel(s)}
                </option>
              ))}
            </select>
          </label>
          <label className="wide">
            {t('ltoNotes')}
            <textarea rows={2} maxLength={2000} value={form.notes} onChange={set('notes')} disabled={ro} />
          </label>
          {!ro && (
            <div className="wide lto-form-row">
              <button type="submit" className="lto-btn primary">
                💾 {t('save')}
              </button>
              {saved && <span className="lto-ok inline">✓ {t('ltoTapeSaved')}</span>}
            </div>
          )}
        </form>
        {error && <div className="lto-err">{error}</div>}
        <div className="lto-form-title">
          {t('ltoOnThisTape')} ({d.items.length})
        </div>
        <div className="lto-tape-items">
          {d.items.map((it) => (
            <button key={it.id} type="button" className="lto-tape-item" onClick={() => onOpenItem(it.id, tp.id)}>
              <span className="lto-ti-no">{it.itemNo ?? '·'}</span>
              <span className="lto-ti-title">
                {it.title}
                <small className="muted">
                  {fmtDateInput(it.eventDate)} · {hhmmss(it.durationS)}
                  {it.fileName ? ` · ${it.fileName}` : ''}
                </small>
              </span>
              {it.hasProxy && <span className="lto-tag">▶</span>}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}
